import * as React from "react"
import { useEffect, useMemo, useState } from "react"
import {
	Alert, Box, Card, CardContent, CircularProgress, Divider,
	MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
	TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material"

import { BACKEND_APIS } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"

import { vtApi as api } from "../api"
import { isoMinusDays, todayISO } from "../metricsUtils"


// Default date window: last 30 days. The platform team's typical
// question is "what did the last month cost?", and a 30-day default
// lines up with monthly invoicing without forcing the user to pick.

function fmtUsd(n) {
	const v = Number(n || 0)
	if (v === 0) return '$0.00'
	if (v < 0.01) return `$${v.toFixed(4)}`
	return `$${v.toFixed(2)}`
}
function fmtInt(n) {
	return new Intl.NumberFormat('en-US').format(Math.round(Number(n || 0)))
}


export default function AiCostsPage() {
	const { showToast } = useAppToast()

	const [startDate, setStartDate] = useState(isoMinusDays(30))
	const [endDate, setEndDate] = useState(todayISO())
	const [idRetailer, setIdRetailer] = useState('')
	const [retailers, setRetailers] = useState([])
	const [loading, setLoading] = useState(false)
	const [data, setData] = useState(null)

	// Retailer catalog drives the dropdown AND the by_retailer table
	// (the back returns id_retailer only — we join client-side for the
	// human name so the dashboard stays a single round-trip).
	const retailerName = useMemo(() => {
		const m = {}
		for (const r of retailers) m[r.id_retailer] = r.name
		return m
	}, [retailers])

	const loadRetailers = async () => {
		const r = await api.get(`${BACKEND_APIS.VT}/admin/retailers`)
		if (!r.ok) return showToast.error('Failed to load retailers: ' + r.error)
		setRetailers(r.data?.items || [])
	}

	const load = async () => {
		setLoading(true)
		const params = new URLSearchParams()
		if (startDate) params.set('start_date', startDate)
		if (endDate)   params.set('end_date', endDate)
		if (idRetailer) params.set('id_retailer', String(idRetailer))
		const r = await api.get(`${BACKEND_APIS.VT}/admin/ai-costs?${params.toString()}`)
		setLoading(false)
		if (!r.ok) {
			showToast.error('Failed to load AI costs: ' + r.error)
			setData(null)
			return
		}
		setData(r.data || null)
	}

	useEffect(() => { loadRetailers() }, [])
	// Auto-refresh on filter change. Cheap query and the user expects
	// the page to live-update — no apply button.
	useEffect(() => { load() }, [startDate, endDate, idRetailer])

	return <PageWrapper
		{...vtPageProps('AI Costs', 'OpenAI token usage and dollar cost by retailer, section and element kind')}
	>
		<Filters
			startDate={startDate} setStartDate={setStartDate}
			endDate={endDate}     setEndDate={setEndDate}
			idRetailer={idRetailer} setIdRetailer={setIdRetailer}
			retailers={retailers}
		/>

		<Box sx={{ mt: 2 }}>
			{loading ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
					<CircularProgress size={22} />
					<Typography variant="body2">Loading AI cost data…</Typography>
				</Box>
			) : !data ? (
				<Alert severity="info">No data for this filter combination.</Alert>
			) : (
				<>
					<SummaryCards summary={data.summary} />
					<Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mt: 2 }}>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<ByDateTable rows={data.by_date} />
						</Box>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<ByRetailerTable rows={data.by_retailer} retailerName={retailerName} />
						</Box>
					</Stack>
					<Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mt: 2 }}>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<ByKindTable rows={data.by_kind} />
						</Box>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<ExpensiveTasksTable rows={data.expensive_tasks} retailerName={retailerName} />
						</Box>
					</Stack>
				</>
			)}
		</Box>
	</PageWrapper>
}


function Filters({ startDate, setStartDate, endDate, setEndDate, idRetailer, setIdRetailer, retailers }) {
	return <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
		<TextField size="small" label="From" type="date" sx={{ width: 180 }}
			value={startDate}
			onChange={e => setStartDate(e.target.value)}
			InputLabelProps={{ shrink: true }}
		/>
		<TextField size="small" label="To" type="date" sx={{ width: 180 }}
			value={endDate}
			onChange={e => setEndDate(e.target.value)}
			InputLabelProps={{ shrink: true }}
		/>
		<TextField size="small" select label="Retailer" sx={{ width: 220 }}
			value={idRetailer}
			onChange={e => setIdRetailer(e.target.value)}
		>
			<MenuItem value="">All retailers</MenuItem>
			{retailers.map(r => (
				<MenuItem key={r.id_retailer} value={r.id_retailer}>{r.name}</MenuItem>
			))}
		</TextField>
	</Stack>
}


function SummaryCards({ summary }) {
	const s = summary || {}
	const avgPerCall = s.calls ? (s.cost_usd / s.calls) : 0
	const hitRate = Number(s.cache_hit_rate || 0)
	const cards = [
		{ label: 'Total cost',        value: fmtUsd(s.cost_usd) },
		{ label: 'AI calls',          value: fmtInt(s.calls) },
		{ label: 'Avg cost / call',   value: fmtUsd(avgPerCall) },
		{ label: 'Prompt tokens',     value: fmtInt(s.prompt_tokens) },
		{ label: 'Completion tokens', value: fmtInt(s.completion_tokens) },
		{ label: 'Total tokens',      value: fmtInt(s.total_tokens) },
		{ label: 'Cache hit',         value: `${(hitRate * 100).toFixed(1)}%` },
	]
	return <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
		{cards.map(c => (
			<Card key={c.label} variant="outlined" sx={{ minWidth: 180, flex: '1 1 180px' }}>
				<CardContent sx={{ '&:last-child': { pb: 2 } }}>
					<Typography variant="caption" color="text.secondary">{c.label}</Typography>
					<Typography variant="h6" sx={{ mt: 0.5 }}>{c.value}</Typography>
				</CardContent>
			</Card>
		))}
	</Stack>
}


function SectionTitle({ children }) {
	return <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>{children}</Typography>
}


function ByDateTable({ rows }) {
	return <Paper variant="outlined" sx={{ p: 2 }}>
		<SectionTitle>By date</SectionTitle>
		{!rows || rows.length === 0 ? (
			<Alert severity="info">No data.</Alert>
		) : (
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Date</TableCell>
							<TableCell align="right">Calls</TableCell>
							<TableCell align="right">Tokens</TableCell>
							<TableCell align="right">Cost</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map(r => (
							<TableRow key={r.task_date}>
								<TableCell>{r.task_date}</TableCell>
								<TableCell align="right">{fmtInt(r.calls)}</TableCell>
								<TableCell align="right">{fmtInt(r.total_tokens)}</TableCell>
								<TableCell align="right">{fmtUsd(r.cost_usd)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		)}
	</Paper>
}


function ByRetailerTable({ rows, retailerName }) {
	return <Paper variant="outlined" sx={{ p: 2 }}>
		<SectionTitle>By retailer</SectionTitle>
		{!rows || rows.length === 0 ? (
			<Alert severity="info">No data.</Alert>
		) : (
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Retailer</TableCell>
							<TableCell align="right">Calls</TableCell>
							<TableCell align="right">Tokens</TableCell>
							<TableCell align="right">Cost</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map(r => (
							<TableRow key={r.id_retailer}>
								<TableCell>{retailerName[r.id_retailer] || `#${r.id_retailer}`}</TableCell>
								<TableCell align="right">{fmtInt(r.calls)}</TableCell>
								<TableCell align="right">{fmtInt(r.total_tokens)}</TableCell>
								<TableCell align="right">{fmtUsd(r.cost_usd)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		)}
	</Paper>
}


function ByKindTable({ rows }) {
	return <Paper variant="outlined" sx={{ p: 2 }}>
		<SectionTitle>By kind</SectionTitle>
		{!rows || rows.length === 0 ? (
			<Alert severity="info">No data.</Alert>
		) : (
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Kind</TableCell>
							<TableCell>Parent kind</TableCell>
							<TableCell align="right">Calls</TableCell>
							<TableCell align="right">Avg cost</TableCell>
							<TableCell align="right">Cost</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((r, i) => {
							const avg = r.calls ? r.cost_usd / r.calls : 0
							return <TableRow key={`${r.kind}-${r.parent_kind}-${i}`}>
								<TableCell>{r.kind}</TableCell>
								<TableCell>{r.parent_kind || '—'}</TableCell>
								<TableCell align="right">{fmtInt(r.calls)}</TableCell>
								<TableCell align="right">{fmtUsd(avg)}</TableCell>
								<TableCell align="right">{fmtUsd(r.cost_usd)}</TableCell>
							</TableRow>
						})}
					</TableBody>
				</Table>
			</TableContainer>
		)}
	</Paper>
}


function ExpensiveTasksTable({ rows, retailerName }) {
	return <Paper variant="outlined" sx={{ p: 2 }}>
		<SectionTitle>Most expensive tasks (top 20)</SectionTitle>
		{!rows || rows.length === 0 ? (
			<Alert severity="info">No data.</Alert>
		) : (
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Date</TableCell>
							<TableCell>Retailer</TableCell>
							<TableCell>Task</TableCell>
							<TableCell align="right">Calls</TableCell>
							<TableCell align="right">Cost</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map(r => (
							<TableRow key={r.id_task}>
								<TableCell>{r.task_date}</TableCell>
								<TableCell>{retailerName[r.id_retailer] || `#${r.id_retailer}`}</TableCell>
								<TableCell>
									<Tooltip title={`section ${r.id_section}`}>
										<span>#{r.id_task}</span>
									</Tooltip>
								</TableCell>
								<TableCell align="right">{fmtInt(r.calls)}</TableCell>
								<TableCell align="right">{fmtUsd(r.cost_usd)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		)}
	</Paper>
}
